/* eslint-disable max-lines */
import React, { useEffect, useRef, useState } from 'react'

import { equals } from 'ramda'
import { components } from 'react-select'
import styled, { createGlobalStyle } from 'styled-components'

import { useEventListener } from '_hooks'
import { transitions } from '_styles/variables'

import { getComponentClass } from '../functions'

const ValueContainerStyles = createGlobalStyle`
  & ${getComponentClass('valueContainer')} {
    padding: 2px 8px;
  }

  & ${getComponentClass('valueContainer', 'is-shrunk')} {
    padding-right: 1.5rem;
  }

  & ${getComponentClass('multiValue')} {
    opacity: 0;
    position: absolute;
    pointer-events: none;
    transition: ${transitions.quick('opacity')};
    max-width: max-content;
    width: 40%;
    flex: 1 1 auto;
  }

  & ${getComponentClass('multiValue', 'is-shown')} {
    opacity: 1;
    position: static;
    pointer-events: all;
  }

  & ${getComponentClass('inputContainer')} {
    overflow: hidden;
    width: 20%;

    & [class$="is-shrunk"] {
      max-width: 20%;
      flex: 1 1 auto;
    }
  }

  & [class$="is-stretched"] {
    flex: 2 0 auto;
    max-width: unset;

    &${getComponentClass('multiValue')} {
      max-width: max-content;
    }
  }
`

const NumElementsDiv = styled.div`
  position: absolute;
  right: 0.25rem;
`

export const ValueContainer: typeof components.ValueContainer = props => {
  const container = useRef<HTMLDivElement>(null)
  const [elementsInNextLines, setElementsInNextLines] = useState(0)
  const [timeout, setTimeoutCode] = useState<NodeJS.Timeout>()
  const { children, innerProps, getValue } = props

  const values = getValue()
  const prevValues = useRef<typeof values>()

  const newProps: typeof props = {
    ...props,
    innerProps: { ...innerProps, ref: container },
  }

  const calculateOptionsShown = () => {
    const { current } = container
    if (current) {
      const elements = Array.from(
        current.querySelectorAll(getComponentClass('multiValue'))
      )

      const inputContainer = current.querySelector(
        getComponentClass('inputContainer')
      )

      const shownMultiValueClass = getComponentClass(
        'multiValue',
        'is-shown',
        true
      )

      if (elements.length <= 1) {
        elements.forEach(el => {
          el.classList.add(shownMultiValueClass)
          el.removeAttribute('aria-hidden')
        })

        if (inputContainer) {
          inputContainer.classList.remove(
            getComponentClass('inputContainer', 'is-shrunk', true)
          )
        }
        current.classList.remove(
          getComponentClass('valueContainer', 'is-shrunk', true)
        )
        setElementsInNextLines(0)

        return
      }
      current.classList.add(
        getComponentClass('valueContainer', 'is-shrunk', true)
      )

      let multiValueWidth = 0
      let numHiddenElements = 0
      let numShownElements = 0
      const maxWidth = 0.75 * current.clientWidth

      elements.forEach(el => {
        const { clientWidth } = el

        if (
          el.classList.contains(
            getComponentClass('multiValue', 'is-shown', true)
          )
        ) {
          numShownElements += 1
          multiValueWidth +=
            0.4 * current.clientWidth > clientWidth
              ? clientWidth
              : 0.4 * current.clientWidth
          return
        }

        if (multiValueWidth + clientWidth <= maxWidth) {
          el.classList.add(shownMultiValueClass)
          el.removeAttribute('aria-hidden')

          numShownElements += 1
          multiValueWidth += clientWidth
        } else {
          numHiddenElements += 1
          el.classList.remove(shownMultiValueClass)
          el.setAttribute('aria-hidden', 'true')
        }
      })
      setElementsInNextLines(numHiddenElements)

      if (inputContainer) {
        const shownElements = Array.from(
          current.querySelectorAll(getComponentClass('multiValue', 'is-shown'))
        )

        if (numShownElements <= 1) {
          shownElements.forEach(el => {
            el.classList.add(
              getComponentClass('multiValue', 'is-stretched', true)
            )
          })
          inputContainer.classList.remove(
            getComponentClass('inputContainer', 'is-shrunk', true)
          )
        } else {
          shownElements.forEach(el => {
            el.classList.remove(
              getComponentClass('multiValue', 'is-stretched', true)
            )
          })
          inputContainer.classList.add(
            getComponentClass('inputContainer', 'is-shrunk', true)
          )
        }
      }
    }
  }

  useEffect(() => {
    if (!prevValues.current || !equals(prevValues.current, values)) {
      calculateOptionsShown()

      prevValues.current = values
    }
  }, [values])

  const calculateOptionsShownDebounced = () => {
    if (timeout) {
      clearTimeout(timeout)
    }

    const newTimeout = setTimeout(calculateOptionsShown, 100)
    setTimeoutCode(newTimeout)
  }

  useEventListener('resize', calculateOptionsShownDebounced)

  return (
    <components.ValueContainer {...newProps}>
      <ValueContainerStyles />
      {children}
      {elementsInNextLines === 0 ? null : (
        <NumElementsDiv>+{elementsInNextLines}</NumElementsDiv>
      )}
    </components.ValueContainer>
  )
}
